import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { mdiReceiptText } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import DataTable from "components/DataTable";
import { WALLET_HISTORY_HEADER_LIST } from "constants/haulpay-broker";
import BrokerWalletHistoryItem from "features/main/HaulPay/modules/Broker/WalletHistory";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { IWalletHistoryItem } from "models/HaulPay";
import {
  getHistoryTransactionAPI,
  getGenericNextPageAPI,
  stripeRegulatoryReceipt
} from "services/api/haulpay";
import styles from "./style.module.scss";

export default function BrokerWalletHistory() {
  const { isDesktop } = useDeviceType();
  const [totalCount, setTotalCount] = useState(0);
  const [nextLink, setNextLink] = useState("nextLink");
  const [prevLink, setPrevLink] = useState("prevLink");

  const [transactionList, setTransactionList] = useState<IWalletHistoryItem[]>([]);

  useEffect(() => {
    const fetchHistoryData = async () => {
      const response = await getHistoryTransactionAPI();
      setTotalCount(response.data.count);
      setNextLink(response.data.next);
      setPrevLink(response.data.previous);
      setTransactionList(response?.data.results);
    };

    fetchHistoryData();
  }, []);

  const fetchMoreHistoryData = async (itemsPerPage: number, pageNumber?: number) => {
    try {
      let response;
      if (itemsPerPage > 0) {
        response = await getGenericNextPageAPI(nextLink ?? "/cmftstripe/transactions/", {
          limit: itemsPerPage,
          ...(pageNumber && { page: pageNumber })
        });
      } else {
        response = await getGenericNextPageAPI(prevLink ?? "/cmftstripe/transactions/", {
          limit: -itemsPerPage,
          ...(pageNumber && { page: pageNumber })
        });
      }
      setTotalCount(response.data.count);
      setNextLink(response.data.next);
      setPrevLink(response.data.previous);
      if (isDesktop) {
        setTransactionList(response.data.results);
      } else {
        setTransactionList([...transactionList, ...response.data.results]);
      }
    } catch {
      showErrorMessage("Failed to fetch statement history. Please try again later.");
    }
  };

  const fetchTransactionReceipt = useCallback(
    async (obligationId: string) => {
      try {
        const res = await stripeRegulatoryReceipt(obligationId);
        if (!res?.data?.regulatory_receipt_url) {
          return showErrorMessage("No receipt found.");
        }
        const newWindow = window.open(res?.data?.regulatory_receipt_url, "_blank");
        if (newWindow) {
          newWindow.focus();
        } else {
          throw "";
        }
      } catch (e) {
        showErrorMessage("Error getting receipt.");
      }
    },
    [transactionList]
  );

  return (
    <div className={styles.walletContainer}>
      <div>
        {isDesktop ? (
          <DataTable
            headers={[
              ...WALLET_HISTORY_HEADER_LIST,
              {
                value: "obligation_id",
                label: "",
                render: (obligationId: string) => (
                  <button
                    type="button"
                    className="!ml-4"
                    onClick={() => fetchTransactionReceipt(obligationId)}>
                    <Icon key={obligationId} path={mdiReceiptText} />
                  </button>
                )
              }
            ]}
            data={transactionList}
            totalCount={totalCount}
            nextLink={nextLink as any}
            prevLink={prevLink as any}
            handlePageChange={fetchMoreHistoryData}
          />
        ) : (
          <>
            {transactionList.length > 0 ? (
              transactionList.map((item) => (
                <BrokerWalletHistoryItem key={item.contract_id} data={item} />
              ))
            ) : (
              <p className="text-center text-placeholder">No Transactions</p>
            )}
          </>
        )}
      </div>
      <Button
        variant="outline"
        className={clsx(styles.loadMoreButton, transactionList.length <= 0 && "hidden")}
        onClick={() => {
          if (nextLink) fetchMoreHistoryData(20);
        }}>
        Load More
      </Button>
    </div>
  );
}
